import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { CTASection } from "@/components/CTASection";
import { RelatedServicesSection } from "@/components/RelatedServicesSection";
import { Thermometer, Snowflake, Euro, Leaf, Settings, Award } from "lucide-react";
import { SEO } from "@/components/seo/SEO";
import { ServiceSchema } from "@/components/seo/ServiceSchema";
import { FAQSchema } from "@/components/seo/FAQSchema";
import { BreadcrumbSchema } from "@/components/seo/BreadcrumbSchema";

const Warmtepompen = () => {
  const faqData = [
    {
      question: "Wat kost een warmtepomp installatie?",
      answer: "Een volledige warmtepomp installatie kost gemiddeld €8.000-€20.000, afhankelijk van het type warmtepomp en de omvang van uw woning. Met ISDE subsidie tot €12.000 wordt dit een zeer aantrekkelijke investering."
    },
    {
      question: "Hoeveel subsidie krijg ik op een warmtepomp?",
      answer: "Via de ISDE-subsidie kunt u tot €12.000 subsidie ontvangen op een warmtepomp. De exacte hoogte hangt af van het type warmtepomp en uw situatie. SOL-VI regelt de subsidieaanvraag voor u."
    },
    {
      question: "Kan een warmtepomp ook koelen in de zomer?",
      answer: "Ja, moderne warmtepompen kunnen zowel verwarmen als koelen. In de zomer werkt het systeem omgekeerd en kan het uw woning effectief koelen, net als een airconditioning."
    },
    {
      question: "Werkt een warmtepomp ook bij vrieskou?",
      answer: "Moderne lucht-water warmtepompen werken uitstekend tot -20°C. Ze zijn speciaal ontworpen voor het Nederlandse klimaat en leveren ook bij lage temperaturen efficiënt warmte."
    }
  ];

  const breadcrumbItems = [
    { name: "Home", url: "https://sol-vi.nl" },
    { name: "Diensten", url: "https://sol-vi.nl/diensten" },
    { name: "Warmtepompen", url: "https://sol-vi.nl/warmtepompen" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <SEO
          title="Warmtepomp Kosten & ISDE Subsidie 2025 | All Electric | SOL-VI"
          description="🔥 Warmtepomp installatie vanaf €8.000 💰 Tot €12.000 ISDE subsidie ❄️ Verwarmen & koelen in één systeem 🌱 Van het gas af 📞 Gratis warmtescan Gelderland"
          keywords="warmtepomp kosten 2025, warmtepomp installatie Elst, all electric warmtepomp, ISDE subsidie warmtepomp, van het gas af, warmtepomp Gelderland, lucht water warmtepomp, warmtepomp COP"
          canonical="https://sol-vi.nl/warmtepompen"
          type="website"
        />
        <ServiceSchema
          name="Warmtepomp Installatie All Electric"
          description="Professionele installatie van energiezuinige warmtepompen voor verwarming en koeling. Van het gas af met ISDE subsidie tot €12.000."
          provider="SOL-VI"
          areaServed={["Elst", "Arnhem", "Nijmegen", "Gelderland", "Betuwe", "Overbetuwe"]}
          serviceType="Heat Pump Installation"
          url="https://sol-vi.nl/warmtepompen"
          offers={[
            { name: "Lucht-Water Warmtepomp", description: "All electric systeem voor verwarming en koeling", priceRange: "€8000-€15000" },
            { name: "Bodem Warmtepomp", description: "Gesloten bodemplussen systeem", priceRange: "€15000-€25000" }
          ]}
        />
        <BreadcrumbSchema items={breadcrumbItems} />
        <FAQSchema faqs={faqData} />
        {/* Hero Section */}
        <div className="pt-20 pb-12 bg-gradient-subtle">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Warmtepomp Kosten 2025: Van het Gas Af met €12.000 ISDE Subsidie
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                All electric warmtepomp installatie in Gelderland. 4x energiezuiniger dan uw CV-ketel met volledige verwarming én koeling.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="hero" size="lg" onClick={() => (window as any).renderOpenSolarWidget()}>
                  Vraag Gratis Offerte
                </Button>
                <Button variant="outline" size="lg" onClick={() => (window as any).Calendly.initPopupWidget({url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800', pageSettings: {backgroundColor: 'ffffff', hideEventTypeDetails: false, hideLandingPageDetails: false, primaryColor: 'ff7800', textColor: '4d5055'}, prefill: {}, utm: {}})}>
                  Plan Adviesgesprek
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              
              {/* Introduction */}
              <div className="mb-12">
                <p className="text-lg leading-relaxed text-muted-foreground">
                  Gasloos wonen begint met een slimme warmtepomp. Dit innovatieve systeem haalt warmte uit de buitenlucht, bodem of grondwater en gebruikt dit om uw woning op een zeer energiezuinige manier te verwarmen. In de zomer werkt het proces andersom en kan de warmtepomp uw huis heerlijk koelen. Stap over op de meest efficiënte manier van klimaatbeheersing.
                </p>
              </div>

              {/* How it works */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-6">Hoe werkt een warmtepomp?</h2>
                <p className="text-lg leading-relaxed text-muted-foreground">
                  Een warmtepomp werkt als een 'omgekeerde koelkast'. Het systeem onttrekt warmte aan een natuurlijke bron (zoals de buitenlucht) en transporteert deze naar binnen. Met behulp van een kleine hoeveelheid elektriciteit wordt deze warmte op de juiste temperatuur gebracht voor uw verwarmingssysteem (vloerverwarming of radiatoren) en voor warm tapwater.
                </p>
              </div>

              {/* Benefits */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-8">De voordelen van een SOL-VI warmtepomp:</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Settings className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">4x Energiezuiniger</h3>
                      <p className="text-muted-foreground">Levert voor elke kWh stroom gemiddeld 4 kWh aan warmte terug. Een CV-ketel haalt uit 1 m³ gas slechts 0,9 kWh warmte.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Thermometer className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Verwarmen én Koelen</h3>
                      <p className="text-muted-foreground">Geniet het hele jaar door van een comfortabel binnenklimaat met één systeem.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Euro className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Hoge Subsidies</h3>
                      <p className="text-muted-foreground">De overheid stimuleert de overstap naar duurzame warmte. Profiteer van de aantrekkelijke ISDE-subsidie.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Leaf className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Gasloos en CO2-neutraal</h3>
                      <p className="text-muted-foreground">Verminder uw CO2-uitstoot drastisch en word volledig onafhankelijk van aardgas, zeker in combinatie met zonnepanelen.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Process */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-8">Onze Werkwijze: Comfort op Maat</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      1
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Warmtescan & Advies</h3>
                      <p className="text-muted-foreground">We analyseren de isolatie van uw woning en uw warmtebehoefte om het perfecte type warmtepomp te selecteren.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      2
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Heldere Offerte</h3>
                      <p className="text-muted-foreground">U ontvangt een gedetailleerd voorstel, inclusief informatie over subsidies en terugverdientijd.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      3
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Professionele Installatie</h3>
                      <p className="text-muted-foreground">Onze specialisten installeren de warmtepomp en zorgen voor een perfecte integratie met uw huidige afgiftesysteem.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      4
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Onderhoud & Service</h3>
                      <p className="text-muted-foreground">Wij bieden servicecontracten om uw systeem in topconditie te houden voor een maximaal rendement.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Final CTA */}
              <div className="text-center bg-muted/50 rounded-lg p-8">
                <h2 className="text-2xl font-bold mb-4">Klaar voor duurzaam comfort?</h2>
                <p className="text-muted-foreground mb-6">Ontdek welke warmtepomp het beste bij uw woning past.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button variant="hero" size="lg" onClick={() => (window as any).renderOpenSolarWidget()}>
                    Vraag een vrijblijvende offerte aan
                  </Button>
                  <Button variant="outline" size="lg" onClick={() => (window as any).Calendly.initPopupWidget({url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800', pageSettings: {backgroundColor: 'ffffff', hideEventTypeDetails: false, hideLandingPageDetails: false, primaryColor: 'ff7800', textColor: '4d5055'}, prefill: {}, utm: {}})}>
                    Plan uw gratis adviesgesprek
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* FAQ Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Veelgestelde Vragen over Warmtepompen</h2>
              <div className="space-y-6">
                {faqData.map((faq, index) => (
                  <div key={index} className="bg-background rounded-lg p-6 shadow-sm">
                    <h3 className="text-lg font-semibold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <RelatedServicesSection currentService="warmtepompen" />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default Warmtepompen;